export interface Coordinates {
  latitude: number
  longitude: number
}

export type GeolocationFailure = 'unsupported' | 'denied' | 'unavailable' | 'timeout'

export class GeolocationError extends Error {
  readonly reason: GeolocationFailure

  constructor(reason: GeolocationFailure) {
    super(`geolocation:${reason}`)
    this.name = 'GeolocationError'
    this.reason = reason
  }
}

export function geolocationErrorKey(error: unknown): string {
  if (error instanceof GeolocationError) {
    switch (error.reason) {
      case 'unsupported':
        return 'discovery.city.errors.unsupported'
      case 'denied':
        return 'discovery.city.errors.denied'
      case 'timeout':
        return 'discovery.city.errors.timeout'
      default:
        return 'discovery.city.errors.unavailable'
    }
  }
  return 'discovery.city.errors.unavailable'
}

/**
 * Persisted discovery filters keep a city-level center, not the raw device
 * position. Three decimals (~100 m) is enough for a radius search and avoids
 * storing a precise home coordinate in localStorage.
 */
export function approximateCoordinates(
  coordinates: Coordinates,
): Coordinates {
  const round = (value: number) => Math.round(value * 1000) / 1000
  return {
    latitude: round(coordinates.latitude),
    longitude: round(coordinates.longitude),
  }
}


/**
 * Foreground browser/Tauri geolocation. No watch and no background tracking
 * (UI_PARITY §7).
 *
 * `78b0868` (browser_location_web.dart): city-level proximity does not need
 * navigation-grade accuracy, but browsers need time to resolve a fix. Discovery
 * requests it in the background on load (never blocking the first list) and
 * from explicit GPS actions; reuse a recent fix, fail the browser call after
 * 20 s and enforce a 21 s outer guard.
 */
export const GEOLOCATION_TIMEOUT_MS = 20_000
export const GEOLOCATION_MAXIMUM_AGE_MS = 60_000
const OUTER_TIMEOUT_MS = 21_000

export function currentCoordinates(
  options: PositionOptions = {
    enableHighAccuracy: false,
    timeout: GEOLOCATION_TIMEOUT_MS,
    maximumAge: GEOLOCATION_MAXIMUM_AGE_MS,
  },
): Promise<Coordinates> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.geolocation ||
    typeof navigator.geolocation.getCurrentPosition !== 'function'
  ) {
    return Promise.reject(new GeolocationError('unsupported'))
  }

  return new Promise((resolve, reject) => {
    let settled = false
    const outerTimer = setTimeout(() => {
      if (settled) return
      settled = true
      reject(new GeolocationError('timeout'))
    }, OUTER_TIMEOUT_MS)

    const finish = (callback: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(outerTimer)
      callback()
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        finish(() => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        })
      },
      (error) => {
        finish(() => {
          if (error.code === error.PERMISSION_DENIED) {
            reject(new GeolocationError('denied'))
            return
          }
          if (error.code === error.TIMEOUT) {
            reject(new GeolocationError('timeout'))
            return
          }
          reject(new GeolocationError('unavailable'))
        })
      },
      options,
    )
  })
}
